import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { UpsertAdminPropertyRequest } from "../../models/RequestModels/UpsertAdminPropertyRequest";
import { AdminPropertyResponse } from "../../models/ResponseModels/AdminPropertyResponse";
import { useAppSelector } from "../../store/hooks";
import AppButton from "../button/AppButton";
import AppTextInput from "../inputs/AppTextInput";

interface UpsertPropertyModalProps {
  open: boolean;
  property: AdminPropertyResponse | null;
  onClose: () => void;
  onSubmit: (body: UpsertAdminPropertyRequest) => void;
}

const emptyForm: UpsertAdminPropertyRequest = {
  name: "",
  address: "",
  city: "",
  notes: "",
};

const UpsertPropertyModal = ({
  open,
  property,
  onClose,
  onSubmit,
}: UpsertPropertyModalProps) => {
  const { saving, fieldErrors } = useAppSelector(
    (state) => state.adminAccounts,
  );

  const [form, setForm] = useState<UpsertAdminPropertyRequest>(emptyForm);

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(
      property
        ? {
            name: property.name,
            address: property.address || "",
            city: property.city || "",
            notes: property.notes || "",
          }
        : emptyForm,
    );
  }, [open, property]);

  const updateField = (
    key: keyof UpsertAdminPropertyRequest,
    value: string,
  ) => {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const getError = (key: string) => fieldErrors[key]?.[0];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{property ? "Edit property" : "Add property"}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <AppTextInput
            label="Property name"
            value={form.name}
            onChange={(value) => updateField("name", value)}
            error={getError("name")}
            required
          />

          <AppTextInput
            label="Address"
            value={form.address || ""}
            onChange={(value) => updateField("address", value)}
            error={getError("address")}
          />

          <AppTextInput
            label="City"
            value={form.city || ""}
            onChange={(value) => updateField("city", value)}
            error={getError("city")}
          />

          <AppTextInput
            label="Notes"
            value={form.notes || ""}
            onChange={(value) => updateField("notes", value)}
            error={getError("notes")}
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </AppButton>

        <AppButton onClick={() => onSubmit(form)} loading={saving}>
          {property ? "Save property" : "Add property"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpsertPropertyModal;
